import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Calendar, Users, Eye, Shield, Clock, RefreshCw } from 'lucide-react';
import api from '../../lib/api';
import { jobUrlSegment } from '../../lib/slug';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { formatDate, cn } from '../../lib/utils';
import { StatNumber, StatsGridSkeleton } from '../../components/ui/StatsLoading';

interface JobAssignment {
  id: string;
  userId: string;
  jobId: string;
  role: 'PRIMARY' | 'COLLABORATOR' | 'REVIEWER';
  assignedAt: string;
  job: {
    id: string;
    title: string;
    description: string;
    status: string;
    deadline: string;
    brandOrg: {
      name: string;
    };
    _count: {
      invitations: number;
    };
  };
  assignedBy: {
    fullName: string | null;
    email: string | null;
  } | null;
}

async function fetchMyAssignments(): Promise<JobAssignment[]> {
  const response = await api.get<JobAssignment[]>('/jobs/my-assignments');
  return response.data;
}

export default function MyAssignmentsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const assignmentsQuery = useQuery({
    queryKey: ['agency', 'my-assignments'],
    queryFn: fetchMyAssignments,
    staleTime: 5 * 60 * 1000,
  });

  const assignments = assignmentsQuery.data ?? [];
  const reloading =
    assignmentsQuery.isFetching && !assignmentsQuery.isPending;

  const filteredAssignments = useMemo(
    () =>
      assignments.filter((assignment) => {
        if (filterRole !== 'all' && assignment.role !== filterRole) return false;
        if (filterStatus !== 'all' && assignment.job.status !== filterStatus) return false;
        return true;
      }),
    [assignments, filterRole, filterStatus]
  );

  const stats = useMemo(() => {
    return {
      total: assignments.length,
      primary: assignments.filter((a) => a.role === 'PRIMARY').length,
      collaborator: assignments.filter((a) => a.role === 'COLLABORATOR').length,
      reviewer: assignments.filter((a) => a.role === 'REVIEWER').length,
      active: assignments.filter((a) => ['OPEN', 'ACTIVE'].includes(a.job.status)).length,
    };
  }, [assignments]);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'secondary' | 'default' | 'success' | 'warning' | 'outline'> = {
      DRAFT: 'secondary',
      OPEN: 'default',
      ACTIVE: 'success',
      PAUSED: 'warning',
      COMPLETED: 'success',
      ARCHIVED: 'outline',
    };
    return variants[status] || 'secondary';
  };

  const getRoleBadge = (role: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'outline'> = {
      PRIMARY: 'default',
      COLLABORATOR: 'secondary',
      REVIEWER: 'outline',
    };
    return variants[role] || 'secondary';
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      PRIMARY: 'Primary Owner',
      COLLABORATOR: 'Collaborator',
      REVIEWER: 'Reviewer',
    };
    return labels[role] || role;
  };

  const assignerName = (a: JobAssignment) =>
    a.assignedBy?.fullName?.trim() ||
    a.assignedBy?.email?.trim() ||
    '—';

  if (assignmentsQuery.isPending) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-[2.25rem] font-semibold tracking-tightest text-glass-text">My Assignments</h1>
          <p className="text-glass-text-2 mt-1">Jobs assigned to you by your team</p>
        </div>
        <StatsGridSkeleton count={5} className="md:grid-cols-5 lg:grid-cols-5" />
        <div className="h-40 rounded-2xl border border-glass animate-pulse bg-glass-2" />
      </div>
    );
  }

  if (assignmentsQuery.isError) {
    return (
      <div className="p-8 text-red-500">
        Could not load assignments. Please try again later.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-[2.25rem] font-semibold tracking-tightest text-glass-text">My Assignments</h1>
          <p className="text-glass-text-2 mt-1">Jobs assigned to you by your team</p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() =>
            queryClient.invalidateQueries({ queryKey: ['agency', 'my-assignments'] })
          }
          disabled={assignmentsQuery.isFetching}
        >
          <RefreshCw className={cn('h-4 w-4 mr-2', reloading && 'animate-spin')} />
          Reload
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {[
          { label: 'Total Jobs', value: stats.total, icon: Briefcase, tone: 'accent' },
          { label: 'Primary Owner', value: stats.primary, icon: Shield, tone: 'cyan' },
          { label: 'Collaborator', value: stats.collaborator, icon: Users, tone: 'purple' },
          { label: 'Reviewer', value: stats.reviewer, icon: Eye, tone: 'emerald' },
          { label: 'Active Jobs', value: stats.active, icon: Clock, tone: 'amber' },
        ].map((s) => {
          const Icon = s.icon;
          const toneClass =
            s.tone === 'accent'
              ? 'text-accent2'
              : s.tone === 'cyan'
              ? 'text-cyan-500 dark:text-cyan-300'
              : s.tone === 'purple'
              ? 'text-purple-500 dark:text-purple-300'
              : s.tone === 'emerald'
              ? 'text-emerald-500 dark:text-emerald-300'
              : 'text-amber-500 dark:text-amber-300';
          return (
            <div key={s.label} className="glass-card p-4">
              <div className={`flex items-center gap-2 text-sm mb-1 ${toneClass}`}>
                <Icon className="w-4 h-4" />
                <span className="text-[0.7rem] uppercase tracking-[0.05em] font-medium">
                  {s.label}
                </span>
              </div>
              <StatNumber loading={reloading}>
                <span className="text-2xl font-semibold text-glass-text tracking-tightest">
                  {s.value}
                </span>
              </StatNumber>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="glass-card p-4">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-glass-text mb-1">
              Filter by Role
            </label>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="glass-input h-10 px-3 text-sm"
            >
              <option value="all">All Roles</option>
              <option value="PRIMARY">Primary Owner</option>
              <option value="COLLABORATOR">Collaborator</option>
              <option value="REVIEWER">Reviewer</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-glass-text mb-1">
              Filter by Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="glass-input h-10 px-3 text-sm"
            >
              <option value="all">All Status</option>
              <option value="DRAFT">Draft</option>
              <option value="OPEN">Open</option>
              <option value="ACTIVE">Active</option>
              <option value="PAUSED">Paused</option>
              <option value="COMPLETED">Completed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Assignments List */}
      {filteredAssignments.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Briefcase className="w-16 h-16 text-glass-text-3 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-glass-text mb-2">
            {assignments.length === 0 ? 'No Assignments Yet' : 'No Matching Assignments'}
          </h3>
          <p className="text-glass-text-2">
            {assignments.length === 0
              ? "You haven't been assigned to any jobs yet. Your manager will assign jobs to you."
              : 'Try adjusting your filters to see more assignments.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredAssignments.map((assignment) => (
            <div
              key={assignment.id}
              className="glass-card p-6 hover:border-accent2/40 transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h3 className="text-lg font-semibold text-glass-text">
                      {assignment.job.title}
                    </h3>
                    <Badge variant={getStatusBadge(assignment.job.status)}>
                      {assignment.job.status}
                    </Badge>
                    <Badge variant={getRoleBadge(assignment.role)}>
                      {getRoleLabel(assignment.role)}
                    </Badge>
                  </div>

                  <p className="text-glass-text-2 mb-4 line-clamp-2">
                    {assignment.job.description}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-glass-text-2">
                      <Briefcase className="w-4 h-4" />
                      <span>{assignment.job.brandOrg?.name ?? '—'}</span>
                    </div>

                    <div className="flex items-center gap-2 text-glass-text-2">
                      <Calendar className="w-4 h-4" />
                      <span>Due: {formatDate(assignment.job.deadline)}</span>
                    </div>

                    <div className="flex items-center gap-2 text-glass-text-2">
                      <Users className="w-4 h-4" />
                      <span>{assignment.job._count.invitations} invites</span>
                    </div>

                    <div className="flex items-center gap-2 text-glass-text-2">
                      <Shield className="w-4 h-4" />
                      <span>Assigned by {assignerName(assignment)}</span>
                    </div>
                  </div>

                  <div className="mt-3 text-xs text-glass-text-3">
                    Assigned on {new Date(assignment.assignedAt).toLocaleDateString()} at{' '}
                    {new Date(assignment.assignedAt).toLocaleTimeString()}
                  </div>
                </div>

                <div className="ml-4">
                  <Button
                    variant="outline"
                    onClick={() =>
                      navigate(`/agency/campaigns/${jobUrlSegment(assignment.job)}`)
                    }
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Job
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
